import Image from 'components/Common/Image'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import OfferList from 'components/OfferList/OfferList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useContext } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { getStepsHowItWorks, getStepsBuyNow, getTermsAndConditionsUrlLink } from './HoolahLandingPageData'
import { connect } from 'react-redux'
import Error404Page from 'components/Pages/Error404Page'
import TextButton from 'LuxKit/Button/TextButton'
import { isHoolahBpEnabled } from 'lib/payment/hoolahBpUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import GeoContext from 'contexts/geoContext'

const HowItWorksContent = styled.div`
  margin-top: ${rem(40)};
  width: 100%;
`

const BuyNowContent = styled.div`
  margin-top: ${rem(48)};
  width: 100%;
`

const AppStoreContent = styled.div`
  margin-top: ${rem(20)};
  width: 100%;
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};

    &.large-margin {
      margin-top: ${rem(92)};
    }
  }
`

const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(32)} ${rem(40)};
  max-width: ${rem(1160)};
  width: 100%;
  height: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(60)} ${rem(32)};
  }
`

const HeroImage = styled(ImageHero)`
  max-height: ${rem(480)};
`

const LogoImage = styled(Image)`
  margin-bottom: ${rem(16)};
  image-rendering: -webkit-optimize-contrast;
  transform: translateZ(0);
`

const StoreImage = styled(Image)`
  margin-top: ${rem(20)};
  margin-right: ${rem(6)};
  margin-left: ${rem(6)};
`

const HeroSectionContent = styled(InnerSection)`
  align-items: center;
  justify-content: center;
  position: relative;
`

const Section = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  max-width: 100%;

  &:nth-child(even) {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const SectionCTA = styled(TextButton)`
  margin-top: ${rem(32)};
`

const SectionBlurb = styled(BodyText).attrs<any>({
  align: 'center',
  colour: 'neutral-eight',
})`
  margin-top: ${rem(16)};
  max-width: ${rem(750)};
  width: 100%;
`

const ProductDescriptors = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    margin-left: -${rem(30)};
  }
`

const ProductDescriptor = styled.div`
  display: flex;
  flex-direction: column;
  /* Fixes IE overflow issue */
  align-items: stretch;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.six};
  padding: ${rem(24)};
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    margin-left: ${rem(30)};
    margin-top: 0;
    height: ${rem(180)};
    flex: 1 1 33%;
  }
`

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > * + * {
    margin-top: ${rem(16)};

    ${mediaQueryUp.tablet} {
      margin-top: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    margin-left: -${rem(32)};
  }
`

const HowItWorksCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  padding: ${rem(110)} ${rem(32)} ${rem(32)};
  max-width: ${rem(415)};
  flex-grow: 1;
  margin-left: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-basis: 100%;
  }
`

const AppLinks = styled(TextLink)`
  display: block;
  padding: 0;
`

const HowItWorksCardImage = styled.div`
  position: absolute;
  top: ${rem(40)};
  left: 0;
  right: 0;
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
`
interface Props {
  currency: string
  theme: any
  isHoolahBpEnabled: boolean
}

function HoolahLandingPage(props: Props) {
  const intl = useIntl()

  React.useEffect(() => {
    window.scroll(0, 0)
  }, [])

  const {
    currency,
    isHoolahBpEnabled,
  } = props

  const { currentRegionCode } = useContext(GeoContext)

  const stepsHowItWorks = getStepsHowItWorks(intl) || []
  const stepsBuyNow = getStepsBuyNow(intl, currency, currentRegionCode) || []
  const termsAndConditionsLink = getTermsAndConditionsUrlLink(currency)
  const shopbackLogoAlt = intl.formatMessage({
    id: 'PqN7Zv2Ju298xBLnG5Dn',
    defaultMessage: 'Shopback',
    description: 'Alt text for the ShopBack logo shown on the ShopBack promotional landing page',
  })

  if (!isHoolahBpEnabled) {
    return <Error404Page invalidRegion />
  }

  return (
    <div>
      <HeadData title={intl.formatMessage({
        id: 'BEP155jsV9wDposJT7kf',
        defaultMessage: 'Shopback',
        description: 'Page title for the Shopback promotional landing page',
      })}
      />
      <HeroImage
        alt=""
        aria-hidden="true"
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="16:5"
        id="fbbg8e2t5ad1yij2s8qf"
        brightness={-30}
      >
        <HeroSectionContent>
          <LogoImage
            height="100"
            id="shopback-white-logo"
            alt={shopbackLogoAlt}
            highRes
            format="svg"
            quality="good"
          />
          <Heading variant="heading3" align="center" colour="neutral-eight">
            {intl.formatMessage({
              id: 'l5CkTYH54yNEEK2nDJeQ',
              defaultMessage: 'Shopback for the wins.',
              description: 'Hero heading on Shopback landing page in the merchandise shop section',
            })}
          </Heading>
          <SectionBlurb variant="large" colour="neutral-eight">
            {intl.formatMessage({
              id: 'gyvTRRkYTCqBPJOflycQ',
              defaultMessage: 'Pay in 3. Interest free. Easy.',
              description: 'Hero blurb on Shopback landing page in the merchandise shop section',
            })}
          </SectionBlurb>
        </HeroSectionContent>
      </HeroImage>
      <Section>
        <InnerSection>
          <Heading variant="heading2" align="center">
            {intl.formatMessage({
              id: 'QxCG_7ueYD7RSh2MOGMM',
              defaultMessage: 'How it works',
              description: 'Section heading for how Shopback works in the merchandise shop section',
            })}
          </Heading>
          <HowItWorksContent>
            <ColumnContainer>
              {stepsHowItWorks.map((step, index) => (
                <HowItWorksCard key={index}>
                  <HowItWorksCardImage>{step.image}</HowItWorksCardImage>
                  <BodyText variant="large" weight="bold">{step.title}</BodyText>
                  <BodyText variant="medium">
                    {step.description}
                  </BodyText>
                </HowItWorksCard>
              ))}
            </ColumnContainer>
            <SubSection>
              <BodyText variant="small" align="center">
                <FormattedMessage
                  id="FLj2dXvxK6NMXS1EIZnU"
                  defaultMessage="Read Shopback’s full <termsLink>Terms & Conditions</termsLink>"
                  description="Text with link to Shopback terms and conditions on Shopback landing page"
                  values={{
                    termsLink: (chunks: React.ReactNode) => (
                      <TextLink weight="regular" target="_blank" href={termsAndConditionsLink}>
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </SubSection>
          </HowItWorksContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3">
            {intl.formatMessage({
              id: 'njFxCz3FI7GzJLEG2Hpx',
              defaultMessage: 'Get the Luxury Escape you need now',
              description: 'Section heading for buy now with Shopback on the promotional landing page',
            })}
          </Heading>
          <BuyNowContent>
            <ProductDescriptors>
              {stepsBuyNow.map((step, index) => (
                <ProductDescriptor key={index}>
                  <Heading variant="heading3">{step.title}</Heading>
                  <BodyText variant="medium">{step.description}</BodyText>
                </ProductDescriptor>
              ))}
            </ProductDescriptors>
          </BuyNowContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3" align="center">
            {intl.formatMessage({
              id: 'lXPUfQk1SvjHaC1-IcYQ',
              defaultMessage: 'Use the LE app to buy now and pay later!',
              description: 'Section heading for LE app section on Shopback landing page',
            })}
          </Heading>
          <AppStoreContent>
            <BodyText variant="large" align="center">
              {intl.formatMessage({
                id: 'Ix91ZZlxl4vd_lFTGtxI',
                defaultMessage: 'Manage your existing orders with the easy-to-use app.',
                description: 'Body text for LE app section on Shopback landing page',
              })}
            </BodyText>
            <ColumnContainer>
              <AppLinks href="https://app.luxuryescapes.com/3WHB/1aab092" target="_blank" rel="noopener">
                <StoreImage id="qy1f0pkl6sbukmkbwayf" height="48" quality="good" format="svg" />
              </AppLinks>
              <AppLinks href="https://app.luxuryescapes.com/3WHB/feb04c69" target="_blank" rel="noopener">
                <StoreImage id="p0qt65l8f6tzobhdkh" height="48" quality="good" format="svg" />
              </AppLinks>
            </ColumnContainer>
          </AppStoreContent>

        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <SubSection>
            <Heading variant="heading3" align="center" as="h2">
              {intl.formatMessage({
                id: 'uUgQtOQNLA2jMUvVSfD_',
                defaultMessage: 'Deals of the week',
                description: 'Section heading for deals on Shopback landing page',
              })}
            </Heading>
            <BuyNowContent>
              <OfferList
                maxOffers={4}
                filters={{
                  offerTypes: FLASH_OFFERS_TYPES,
                  frontPageVisibleOnly: true,
                }}
              />
            </BuyNowContent>
            <SectionCTA kind="primary" href="/">
              {intl.formatMessage({
                id: 'SXsTXUnBMjHALjYETEHn',
                defaultMessage: 'Discover all amazing deals',
                description: 'Button text to discover all deals on Shopback landing page',
              })}
            </SectionCTA>
          </SubSection>
        </InnerSection>
      </Section>
    </div>
  )
}

const mapStateToProps = (state: App.State) => {
  const currency = state.geo.currentCurrency

  return {
    currency,
    isHoolahBpEnabled: isHoolahBpEnabled(currency),
  }
}

export default connect(mapStateToProps)(HoolahLandingPage)
